import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BrandLoader } from '../components/BrandLoader';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, AlertCircle, CheckCircle, ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        await axios.get(`${API_URL}/auth/verify-reset-token`, {
          params: { token }
        });
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (verifying) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <BrandLoader size="md" label="Verifying reset link…" />
        </div>
      );
    }

    if (success) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Password Reset Complete</h3>
            <p className="text-slate-600">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
          </div>
          <div className="pt-4">
            <Link to="/login">
              <Button className="w-full bg-blue-900 hover:bg-blue-950" data-testid="login-after-reset-btn">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    if (!tokenValid) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Invalid Reset Link</h3>
            <p className="text-slate-600">
              {error || 'This password reset link is invalid or has expired.'}
            </p>
          </div>
          <div className="pt-4 space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full bg-blue-900 hover:bg-blue-950" data-testid="request-new-link-btn">
                Request a new link
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to login
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-slide-down">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10"
              required
              minLength={6}
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-slate-500">Minimum 6 characters</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 pr-10"
              required
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button 
          type="submit" 
          className="w-full bg-blue-900 hover:bg-blue-950"
          disabled={loading}
          data-testid="reset-password-btn"
        >
          {loading ? 'Resetting...' : 'Reset password'}
        </Button>

        <div className="text-center pt-4">
          <Link 
            to="/login" 
            className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
            data-testid="back-link"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to login
          </Link>
        </div>
      </form>
    );
  };

  return (
    <div className="auth-layout" data-testid="reset-password-page">
      <div className="auth-left">
        <div className="auth-form animate-slide-up">
          <Link
            to="/"
            className="group flex items-center gap-3 mb-8 -ml-1 px-1 py-1 rounded-lg hover:bg-slate-100/60 transition-colors"
            aria-label="Back to home"
            data-testid="reset-home-link"
          >
            <img src="/logo-login.png" alt="ClinicalFlow" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] group-hover:text-[#1e3a5f] transition-colors">ClinicalFlow</h1>
              <p className="text-sm text-slate-500">Research Request Portal</p>
            </div>
          </Link>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-['Manrope']">
                {success ? 'Success!' : 'Reset your password'}
              </CardTitle>
              <CardDescription>
                {success 
                  ? 'Your password has been updated'
                  : 'Enter your new password below'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderContent()}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="auth-right">
        <div className="relative z-10 text-center text-white p-12 max-w-lg">
          <h2 className="text-3xl font-bold mb-4 font-['Manrope']">
            Create a Strong Password
          </h2>
          <p className="text-lg text-blue-100 opacity-90">
            Choose a password that is at least 6 characters long. For better security, use a mix of letters, numbers, and symbols.
          </p>
        </div>
      </div>
    </div>
  );
}
